import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { getUserInfo } from '@/api/user';
import { userInfoAtom, userLoggedInAtom } from '@/jotai/atoms/userAtom';
import { Button, Spin } from 'antd';
import './CallbackPage.css';

const useQueryParams = () => {
  return new URLSearchParams(useLocation().search);
};

const CallbackPage: React.FC = () => {
  const queryParams = useQueryParams();
  const code = queryParams.get('code');
  const navigate = useNavigate();

  const setUserInfo = useSetAtom<any>(userInfoAtom);
  const setUserLoggedIn = useSetAtom(userLoggedInAtom);

  const { data, error, isLoading } = useQuery({
    queryKey: ['getUserInfo', code],
    queryFn: () => getUserInfo(code!),
    enabled: !!code,
    retry: false,
  });

  useEffect(() => {
    if (data && data.status === 200) {
      const { userToken, name, avatarUrl } = data?.data;

      setUserInfo({ id: userToken, name, avatar: avatarUrl });
      setUserLoggedIn(true);

      localStorage.setItem(
        'userInfo',
        JSON.stringify({ id: userToken, name, avatar: avatarUrl })
      );
      localStorage.setItem('userLoggedIn', 'true');

      navigate('/', { replace: true });
    }
  }, [data, setUserInfo, setUserLoggedIn, navigate]);

  const handleGoBack = () => {
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div className="callback-container">
        <div className="callback-card">
          <Spin size="large" />
          <p className="callback-message" style={{ marginTop: '16px' }}>
            Logging you in...
          </p>
        </div>
      </div>
    );
  }

  if (error || (data && data.status !== 200)) {
    const errorMessage = error
      ? 'An unexpected error occurred. Please try again.'
      : data.message;

    return (
      <div className="callback-container">
        <div className="callback-card error">
          <h1 className="callback-title">Error</h1>
          <p className="callback-message">{errorMessage}</p>
          <Button
            type="primary"
            onClick={handleGoBack}
            className="callback-button"
          >
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  if (data && data.status === 200) {
    const { name, avatarUrl, userToken } = data?.data;
    return (
      <div className="callback-container">
        <div className="callback-card">
          <h1 className="callback-title">Welcome, {name}!</h1>
          <img
            src={avatarUrl}
            alt={`${name}'s avatar`}
            className="callback-avatar"
          />
          <p className="callback-token">User Token: {userToken}</p>
        </div>
      </div>
    );
  }

  return null;
};

export default CallbackPage;
